// A declared field drawn with the console's own inputs (02-DESIGN/14-solutions/03-the-console.md
// § the elements: field kinds are the console's and few). A kind the console
// does not know is drawn as text with a note; nothing is guessed.

import { Field as FieldBox, Select, TextArea, TextInput } from "../../ui/components.tsx";
import { FieldKind } from "../../wire/solutions.ts";

/** A declared field, as a setting or a form's field names it. */
export interface Declared {
  name: string;
  label: string;
  kind: string;
  hint?: string;
  options?: string[];
  required?: boolean;
}

/** valueText renders a value as the field edits it. */
export function valueText(v: unknown): string {
  if (v === undefined || v === null) {
    return "";
  }
  return typeof v === "string" ? v : JSON.stringify(v);
}

/** parseValue reads a field's text back into the value the document holds: a number, a toggle, or the text. */
export function parseValue(kind: string, text: string): unknown {
  switch (kind) {
    case FieldKind.number: {
      const n = Number(text);
      return text.trim() === "" || Number.isNaN(n) ? undefined : n;
    }
    case FieldKind.toggle:
      return text === "true";
    default:
      return text === "" ? undefined : text;
  }
}

const KNOWN = new Set<string>([
  FieldKind.text,
  FieldKind.multiline,
  FieldKind.number,
  FieldKind.toggle,
  FieldKind.choice,
  FieldKind.duration,
  FieldKind.time,
  FieldKind.secret,
]);

export function FieldInput({
  f,
  value,
  onChange,
  disabled,
}: {
  f: Declared;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const known = KNOWN.has(f.kind);
  const hint = known
    ? f.hint
    : `${f.hint ?? ""} (drawn as text: this console does not know "${f.kind}")`.trim();
  if (f.kind === FieldKind.toggle) {
    return (
      <label className="checkbox">
        <input
          type="checkbox"
          checked={value === "true"}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.checked ? "true" : "false");
          }}
        />
        <span>{f.label}</span>
      </label>
    );
  }
  let input;
  switch (f.kind) {
    case FieldKind.multiline:
      input = <TextArea value={value} onChange={onChange} rows={4} disabled={disabled} />;
      break;
    case FieldKind.choice:
      input = (
        <Select
          value={value}
          onChange={onChange}
          disabled={disabled}
          options={[{ value: "", label: "—" }, ...(f.options ?? []).map((o) => ({ value: o, label: o }))]}
        />
      );
      break;
    case FieldKind.number:
      input = <TextInput type="number" value={value} onChange={onChange} disabled={disabled} />;
      break;
    case FieldKind.secret:
      input = <TextInput type="password" value={value} onChange={onChange} disabled={disabled} />;
      break;
    default:
      input = (
        <TextInput
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={f.kind === FieldKind.duration ? "60s, 5m, 1h" : undefined}
          mono={f.kind === FieldKind.duration || f.kind === FieldKind.time}
        />
      );
  }
  return (
    <FieldBox label={f.required === true ? f.label : `${f.label} (optional)`} hint={hint}>
      {input}
    </FieldBox>
  );
}
